import { useQueryClient } from '@tanstack/react-query'
import { usePostServicetypes, getGetServicetypesQueryKey } from '@/api/generated/service-type/service-type'
import type { ServiceTypeAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getNewServiceErrorMessage } from './getNewServiceErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Publishes (POST) a new service type. Replaces the `publishService` thunk + PUBLISH_SERVICE*
 * flow.
 *
 * Old side effects, reproduced via the caller's callbacks:
 * - PUBLISH_SERVICE -> loading: exposed via `isPending`.
 * - PUBLISH_SERVICE_SUCCESS -> `publishSuccess` flag (the page watched it to navigate back and
 *   reset): surfaced through `onSuccess`.
 * - PUBLISH_SERVICE_ERROR -> mapped message via `onError(message)`.
 * Invalidates the shared service-types collection so the management list (useServiceTypes)
 * shows the new service type.
 */
export const usePublishService = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = usePostServicetypes()

  const publishService = (
    body: ServiceTypeAdminApiInContract,
    opts?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => {
    mutate(
      { data: body },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetServicetypesQueryKey() })
          opts?.onSuccess?.()
        },
        onError: (error) => {
          opts?.onError?.(getNewServiceErrorMessage(getServiceApiErrorBody(error)))
        },
      },
    )
  }

  return { publishService, isPending }
}
